import { enqueueSnackbar } from 'notistack';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Box, Chip, Typography } from '@mui/material';

import ApiClient from '@/src/utils/ApiClient';
import { IApiClientResponse, ILooseObject } from '@/src/types/common.interface';
import { store } from '@/src/datastore/store';
import AppConfig from '@/src/config/AppConfig';
import TableHeadersConfig from '@/src/config/TableHeadersConfig';
import AllAppLevelCredentials, {
  ALL_APP_LEVEL_CREDENTIALS_URLS_ENUM
} from '@/src/config/api_config/allAppLevelCredentials';
import AppUtils from '@/src/utils/AppUtils';

export interface IGetAppLevelCredentialsListParams {
  filters: ILooseObject;
}

export interface IGetAppLevelCredentialsListResult {
  rows: ILooseObject[];
  columns: GridColDef[];
  error: IApiClientResponse['error'];
}

type CreateNewAppLevelCredentialsResult = {
  data: ILooseObject;
  error: {
    code: number;
    message: string;
  };
};

type CreateNewAppLevelCredentialsParams = {
  payload: ILooseObject;
};

export interface IGetRolesListByDestinationApplicationParams {
  applicationKeys: string;
}

const updateTableColumns = (headers: GridColDef[]): GridColDef[] => {
  return headers.map((header: GridColDef) => {
    if (typeof header === 'object' && header !== null) {
      const tableColumns: GridColDef = {
        ...header
      };

      if (header.field === 'roles') {
        tableColumns.renderCell = (params: GridRenderCellParams) => {
          if (Array.isArray(params?.row?.roles) && params?.row?.roles?.length > 0) {
            return params?.row?.roles?.join(', ');
          }
          return '-';
        };
      } else if (header.field === 'source_application') {
        tableColumns.renderCell = (params: GridRenderCellParams) => {
          const sourceApp = params?.row?.source_application;
          if (sourceApp && typeof sourceApp === 'object') {
            return sourceApp.ui_label || sourceApp.name;
          }
          return '-';
        };
      } else if (header.field === 'destination_application') {
        tableColumns.renderCell = (params: GridRenderCellParams) => {
          const destApp = params?.row?.destination_application;
          if (destApp && typeof destApp === 'object') {
            return destApp.ui_label || destApp.name;
          }
          return '-';
        };
      } else if (header.field === 'expiry_time') {
        tableColumns.renderCell = (params: GridRenderCellParams) => {
          const expiryTime = params?.row?.expiry_time;
          const currentDate = new Date();
          const expiryDate = new Date(expiryTime);
          const isExpired = expiryDate < currentDate;

          if (expiryTime) {
            return (
              <Box>
                <Typography>{AppUtils.formatDateTime(expiryTime)}</Typography>
                {isExpired && <Chip label="Expired" color="error" />}
              </Box>
            );
          }
          return '-';
        };
      }

      return tableColumns;
    } else {
      return {} as GridColDef;
    }
  });
};

const updateDefaultColumns = (applicationKey: string): GridColDef[] => {
  const defaultColumns: GridColDef[] = [];
  defaultColumns.push({
    field: 'Actions',
    headerName: 'Actions',
    sortable: false,
    filterable: false,
    flex: 1,
    minWidth: 150
  });
  return defaultColumns;
};

abstract class AllAppLevelCredentialsService {
  public static async getAllAppLevelCredentialsList(params: IGetAppLevelCredentialsListParams) {
    const { filters } = params;
    const result: IGetAppLevelCredentialsListResult = {
      rows: [],
      columns: [],
      error: {
        code: 0,
        message: ''
      }
    };
    try {
      const response = await ApiClient.makeRequest({
        url: AllAppLevelCredentials.getAllAppLevelCredentialsApiUrl({
          url: ALL_APP_LEVEL_CREDENTIALS_URLS_ENUM.GET_ALL_APP_LEVEL_CREDENTIALS_LIST,
          filters
        }),
        method: 'GET'
      });

      if (response.data) {
        const tableColumns: GridColDef[] = updateTableColumns(
          TableHeadersConfig.ALL_APP_LEVEL_CREDENTIALS_URLS_LIST || []
        );
        const userPermissions = store.getState()?.userDetails?.permissions ?? [];
        const applicationKey = response.data?.data?.application_key;
        const defaultColumns: GridColDef[] = userPermissions?.includes('x_api_key_config_view')
          ? updateDefaultColumns(applicationKey)
          : [];
        result.columns = [...tableColumns, ...defaultColumns];

        const rawData = response.data?.data ?? [];

        const now = Date.now();

        const sortedData = [...rawData].sort((a: ILooseObject, b: ILooseObject) => {
          const aTime = Date.parse(a?.expiry_time);
          const bTime = Date.parse(b?.expiry_time);
          const aExpired = aTime < now;
          const bExpired = bTime < now;

          if (aExpired === bExpired) return aTime - bTime;
          return aExpired ? 1 : -1;
        });

        result.rows = sortedData;
      } else {
        result.error = {
          ...response.error
        };
      }
    } catch (error) {
      enqueueSnackbar(error?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    }
    return result;
  }

  public static async createNewAppLevelCredentials(
    params: CreateNewAppLevelCredentialsParams
  ): Promise<CreateNewAppLevelCredentialsResult> {
    const { payload } = params;

    const result: CreateNewAppLevelCredentialsResult = {
      data: {},
      error: {
        code: 0,
        message: ''
      }
    };

    try {
      const response: IApiClientResponse = await ApiClient.makeRequest({
        url: AllAppLevelCredentials.getAllAppLevelCredentialsApiUrl({
          url: ALL_APP_LEVEL_CREDENTIALS_URLS_ENUM.CREATE_NEW_APP_LEVEL_CREDENTIALS
        }),
        method: 'POST',
        body: payload
      });

      if (response.data) {
        result.data = response.data;
      } else {
        result.error = { ...response.error };
      }
    } catch (error) {
      enqueueSnackbar(error?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    }

    return result;
  }

  public static async getRolesListByDestinationApplications(
    params: IGetRolesListByDestinationApplicationParams
  ) {
    const { applicationKeys } = params;
    const result: IApiClientResponse = {
      data: {},
      error: {
        code: 0,
        message: ''
      }
    };

    try {
      const response = await ApiClient.makeRequest({
        url: AllAppLevelCredentials.getAllAppLevelCredentialsApiUrl({
          url: ALL_APP_LEVEL_CREDENTIALS_URLS_ENUM.GET_ROLES_BY_DESTINATION_APPLICATION,
          filters: { application_keys: applicationKeys }
        }),
        method: 'GET'
      });

      if (response.data) {
        result.data = { ...response.data };
      } else {
        result.error = {
          ...response.error
        };
      }
    } catch (error) {
      enqueueSnackbar(error?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    }
    return result;
  }

  public static async getAllApplications() {
    const result: IApiClientResponse = {
      data: {},
      error: {
        code: 0,
        message: ''
      }
    };

    try {
      const response = await ApiClient.makeRequest({
        url: AllAppLevelCredentials.getAllAppLevelCredentialsApiUrl({
          url: ALL_APP_LEVEL_CREDENTIALS_URLS_ENUM.GET_ALL_APPLICATION_LIST
        }),
        method: 'GET'
      });

      if (response.data) {
        result.data = { ...response.data };
      } else {
        result.error = {
          ...response.error
        };
      }
    } catch (error) {
      enqueueSnackbar(error?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    }
    return result;
  }
}

export default AllAppLevelCredentialsService;
